import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import '../styles/download-dropdown.css';

/**
 * Format labels for display
 */
const formatLabels = {
  dwg: 'DWG (AutoCAD)',
  ifc: 'IFC',
  rfa: 'RFA (Famille Revit)',
  rvt: 'RVT (Revit)',
  xkt: 'XKT (Visualisation)'
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    return `${mb.toFixed(2)} MB`;
  }
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
};

/**
 * DownloadDropdown Component
 * 
 * Displays a "Télécharger" dropdown button that shows available download formats.
 * Only formats that were uploaded by the admin are shown.
 * 
 * @param {Object} props
 * @param {Object} props.availableFormats - Object with format keys and {url, size} values
 * @param {string} props.modelId - The model ID for tracking
 * @param {Function} props.onDownload - Callback when a format is selected (format) => void
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 */
function DownloadDropdown({ availableFormats, modelId, onDownload, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get array of available format keys
  const formatKeys = availableFormats ? Object.keys(availableFormats) : [];
  const hasFormats = formatKeys.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle dropdown toggle
  const handleToggle = () => {
    if (!disabled && hasFormats) {
      setIsOpen(!isOpen);
    }
  };

  // Handle format selection
  const handleFormatClick = (format) => {
    if (onDownload) {
      onDownload(format);
    }
    setIsOpen(false);
  };

  return (
    <div className="download-dropdown-container" ref={dropdownRef}>
      <button
        className={`btn-download btn-download-dropdown ${isOpen ? 'open' : ''} ${!hasFormats ? 'disabled' : ''}`}
        onClick={handleToggle}
        disabled={disabled || !hasFormats}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>Télécharger</span>
        <ChevronDown 
          className={`dropdown-chevron ${isOpen ? 'open' : ''}`} 
          size={18} 
        />
      </button>

      {isOpen && hasFormats && (
        <div className="download-dropdown-menu" role="listbox">
          {formatKeys.map((format) => {
            const formatData = availableFormats[format];
            return (
              <button
                key={format}
                className="download-dropdown-item"
                onClick={() => handleFormatClick(format)}
                role="option"
              >
                <span className="format-badge">{format.toUpperCase()}</span>
                <span className="format-label">{formatLabels[format] || format.toUpperCase()}</span>
                {formatData?.size && (
                  <span className="format-size">{formatFileSize(formatData.size)}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!hasFormats && (
        <span className="no-formats-message">Aucun fichier disponible</span>
      )}
    </div>
  );
}

export default DownloadDropdown;
